// Cloudinary upload helper supporting signed OR unsigned upload
// NOTE: Storing API secrets in the frontend is insecure. Prefer a backend signature endpoint.

// Support either individual vars or single URL vars
// REACT app exposes only REACT_APP_* at runtime, but we'll also check CLOUDINARY_URL for flexibility
const CLOUDINARY_URL = process.env.REACT_APP_CLOUDINARY_URL || process.env.CLOUDINARY_URL;

// Fallback to hardcoded values if envs are not available (NOT secure; for local debugging only)
const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const API_KEY = process.env.REACT_APP_CLOUDINARY_API_KEY;
const API_SECRET = process.env.REACT_APP_CLOUDINARY_API_SECRET ; // Avoid using in frontend in production

// Allow resource type override via env; default to image
const RESOURCE_TYPE = process.env.REACT_APP_CLOUDINARY_RESOURCE_TYPE || 'image';

const CLOUDINARY_UPLOAD_URL = CLOUD_NAME
  ? `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/${RESOURCE_TYPE}/upload`
  : '';

const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET; // for unsigned uploads

// Debug env presence (remove after verifying env setup)
// eslint-disable-next-line no-console
console.info('Cloudinary env present?', {
  CLOUD_NAME: Boolean(CLOUD_NAME),
  API_KEY: Boolean(API_KEY),
  API_SECRET: Boolean(API_SECRET),
  UPLOAD_PRESET: Boolean(UPLOAD_PRESET),
  URL: Boolean(CLOUDINARY_URL),
  RESOURCE_TYPE,
});

async function sha1Hex(message) {
  const encoder = new TextEncoder();
  const data = encoder.encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-1', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

function buildStringToSign(params) {
  // Cloudinary requires params sorted alphabetically, joined with & (exclude file and signature)
  const sortedKeys = Object.keys(params).sort();
  const toJoin = sortedKeys.map((key) => `${key}=${params[key]}`).join('&');
  return toJoin + API_SECRET;
}

async function generateSignature(params) {
  const stringToSign = buildStringToSign(params);
  return await sha1Hex(stringToSign);
}

export async function uploadImageToCloudinary(file, options = {}) {
  // Validate basic requirements
  if (!CLOUD_NAME) {
    throw new Error('Cloudinary env vars missing: REACT_APP_CLOUDINARY_CLOUD_NAME');
  }
  if (!CLOUDINARY_UPLOAD_URL) {
    throw new Error('Cloudinary upload URL could not be constructed');
  }

  const formData = new FormData();
  formData.append('file', file);
  if (options.folder) formData.append('folder', options.folder);
  if (options.publicId) formData.append('public_id', options.publicId);

  // Prefer unsigned upload if preset is configured
  if (UPLOAD_PRESET) {
    formData.append('upload_preset', UPLOAD_PRESET);
  } else {
    // Signed upload path requires API key and secret
    if (!API_KEY || !API_SECRET) {
      throw new Error('Cloudinary env vars missing: REACT_APP_CLOUDINARY_API_KEY and/or REACT_APP_CLOUDINARY_API_SECRET');
    }

    const timestamp = Math.floor(Date.now() / 1000);
    const paramsToSign = {
      timestamp,
      ...(options.folder ? { folder: options.folder } : {}),
      ...(options.publicId ? { public_id: options.publicId } : {}),
    };

    const signature = await generateSignature(paramsToSign);
    formData.append('api_key', API_KEY);
    formData.append('timestamp', String(timestamp));
    formData.append('signature', signature);
  }

  const response = await fetch(CLOUDINARY_UPLOAD_URL, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const errText = await response.text();
    throw new Error(`Cloudinary upload failed: ${response.status} ${errText}`);
  }

  const data = await response.json();
  const selectedUrl = data.secure_url || data.url;
  if (!selectedUrl) {
    throw new Error('Cloudinary upload missing URL in response');
  }
  // Normalize to https if any http url slips through
  return selectedUrl.replace(/^http:\/\//, 'https://');
}

export async function uploadMultipleImages(files, options = {}) {
  const results = [];
  for (const file of files) {
    // Basic size/type validation
    if (!(file && file.type && file.type.startsWith('image/'))) {
      continue;
    }
    const url = await uploadImageToCloudinary(file, options);
    results.push(url);
  }
  return results;
}


